import { ContentItem, SocialMediaLink } from "../types";
import { updateContent, uploadImage } from "../services/firebase";
import React, {
  useState,
  useRef,
  forwardRef,
  useEffect,
  useCallback,
} from "react";
import { deleteDoc, doc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { MarkdownField } from "./ui/MarkDown";
import SocialMediaPreview from "./SocialMediaPreview";
import SocialMediaInput from "./SocialMediaInput";
import { generateSlug } from "./utils/generateSlug";
import CustomSelect from "./ui/CustomSelect";
import Cropper from "react-easy-crop";
import { validateImageFile } from "../components/utils/imageValidation";
import { cropImageFile } from "../components/utils/cropImage";
import { resizeImage } from "./utils/resizeImage";
import CharCounter from "./utils/CharCounter";
import VideoUploader from "./ui/VideoUploader";

interface EditPunishmentProps {
  content: ContentItem;
  cancelEditing: (data: boolean) => void;
  editFrom?: string;
}
const EditPunishment: React.FC<EditPunishmentProps> = ({
  content,
  cancelEditing,
  editFrom,
}) => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(content);
  const [newImage, setNewImage] = useState<File | null>(null);
  const [newCardImage, setNewCardImage] = useState<File | null>(null);
  const [newMobileHeaderImage, setNewMobileHeaderImage] = useState<File | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [tempImageUrl, setTempImageUrl] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 9, y: 16 });
  const [zoom, setZoom] = useState(1);
  const [croppedArea, setCroppedArea] = useState<any>(null);
  const [loadingCropper, setLoadingCropper] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageType, setImageType] = useState<string | null>(null);
  const [instructionImageIndex, setInstructionImageIndex] = useState<
    number | null
  >(null);

  //Refs to clear the cropper selected images
  const imageRef = useRef<HTMLInputElement | null>(null);
  const cardImageRef = useRef<HTMLInputElement | null>(null);
  const mobileHeaderImageRef = useRef<HTMLInputElement | null>(null);
  const [videoUrl, setVideoUrl] = useState<string>(content.videoUrl || "");
  const flRatingOptions = [
    {
      value: "Pee Wee Level 1 | Basic",
      label: ["Pee Wee", "Level 1 | Basic"],
    },
    {
      value: "Two Hand Touch Level 2 | Challenging",
      label: ["Two Hand Touch", "Level 2 | Challenging"],
    },
    {
      value: "Full Contact Level 3 | Serious",
      label: ["Full Contact", "Level 3 | Serious"],
    },
    {
      value: "All Pro Elite Level 4 | Ruthless",
      label: ["All Pro Elite", "Level 4 | Ruthless"],
    },
    {
      value: "No Mercy Level 5 | Insane",
      label: ["No Mercy", "Level 5 | Insane"],
    },
  ];

  // Function to handle input changes in the edit form
  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setEditedContent((prev) => ({
      ...prev,
      [name]:
        name === "type"
          ? (value as "punishment" | "challenge" | "draft")
          : value,
    }));
  };

  const setSubtitleValue = (value: string) => {
    setEditedContent((prev) => ({
      ...prev,
      subtitle: value,
    }));
  };

  const setImageDescriptionValue = (value: string) => {
    setEditedContent((prev) => ({
      ...prev,
      imageDescription: value,
    }));
  };

  // const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //     if (e.target.files && e.target.files[0]) {
  //         setNewImage(e.target.files[0]);
  //     }
  // };

  const handleImageChange = useCallback(
    async (
      e: React.ChangeEvent<HTMLInputElement>,
      imageType: string,
      instructionIndex?: number
    ) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const validationError = validateImageFile(file);
      if (validationError) {
        alert(`${validationError}`);
        setError(validationError);
        return;
      }

      setLoadingCropper(true);
      try {
        const { resizedUrl, resizedFile } = await resizeImage(file);
        setTempImageUrl(resizedUrl);
        setShowCropper(true);
        setError(null);
        if (instructionIndex !== undefined) {
          setInstructionImageIndex(instructionIndex);
        }
      } finally {
        setLoadingCropper(false);
      }
    },
    []
  );

  const onCropComplete = useCallback((_: any, croppedPixels: any) => {
    setCroppedArea(croppedPixels);
  }, []);

  const handleCropConfirm = useCallback(
    async (imageType: string | null) => {
      if (!tempImageUrl || !croppedArea) return;

      const cropped = await cropImageFile(tempImageUrl, croppedArea);
      if (cropped) {
        if (imageType === "main-image") {
          setNewImage(cropped);
        } else if (imageType === "card-image") {
          setNewCardImage(cropped);
        } else if (imageType === "mobile-header-image") {
          setNewMobileHeaderImage(cropped);
        } else if (
          imageType === "instruction-image" &&
          instructionImageIndex !== null
        ) {
          const newInstructions = [...editedContent.instructions];
          newInstructions[instructionImageIndex].image = cropped;
          setEditedContent((prev) => ({
            ...prev,
            instructions: newInstructions,
          }));
        }
        //   setSelectedImage(cropped);
        setImagePreview(URL.createObjectURL(cropped));
      }

      setShowCropper(false);
      setInstructionImageIndex(null);
    },
    [
      tempImageUrl,
      croppedArea,
      instructionImageIndex,
      editedContent.instructions,
    ]
  );

  const setEquipmentLockerValue = (value: string) => {
    setEditedContent((prev) => ({
      ...prev,
      equipmentLocker: value,
    }));
  };

  const handleArrayInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
    field: keyof ContentItem
  ) => {
    const newArray = [...(editedContent[field] as string[])];
    newArray[index] = e.target.value;
    setEditedContent((prev) => ({ ...prev, [field]: newArray }));
  };

  const handleArrayMarkDownInputChange = (
    value: string,
    index: number,
    field: keyof ContentItem
  ) => {
    const newArray = [...(editedContent[field] as string[])];
    newArray[index] = value;
    setEditedContent((prev) => ({ ...prev, [field]: newArray }));
  };

  const handleAddSocialLink = (link: Omit<SocialMediaLink, "id">) => {
    const newLink: SocialMediaLink = {
      ...link,
      id: Math.random().toString(36).substr(2, 9), // Simple unique ID generation
    };

    setEditedContent((prev) => ({
      ...prev,
      socialMediaLinks: [...(prev.socialMediaLinks || []), newLink],
    }));
  };

  const handleDeleteSocialLink = (id: string) => {
    setEditedContent((prev) => ({
      ...prev,
      socialMediaLinks: (prev.socialMediaLinks || []).filter(
        (link) => link.id !== id
      ),
    }));
  };

  const handleRemoveArrayItem = (index: number, field: keyof ContentItem) => {
    const newArray = [...(editedContent[field] as string[])];
    newArray.splice(index, 1);
    setEditedContent((prev) => ({ ...prev, [field]: newArray }));
  };

  const handleSave = async () => {
    try {
      let updatedContent = { ...editedContent };
      updatedContent.videoUrl = videoUrl;
      // Handle main image upload if there's a new image
      if (newImage) {
        const imageUrl = await uploadImage(newImage);
        updatedContent.image = imageUrl;
      }

      if (newCardImage) {
        const cardImageUrl = await uploadImage(newCardImage);
        updatedContent.cardImage = cardImageUrl;
      }

      if (newMobileHeaderImage) {
        const mobileHeaderImageUrl = await uploadImage(newMobileHeaderImage);
        updatedContent.mobileHeaderImage = mobileHeaderImageUrl;
      }

      // Handle equipment image upload if it's a File
      if (updatedContent.equipmentImage instanceof File) {
        const equipmentImageUrl = await uploadImage(
          updatedContent.equipmentImage
        );
        updatedContent.equipmentImage = equipmentImageUrl;
      }

      // Handle equipment product image uploads
      if (
        updatedContent.equipmentProducts &&
        updatedContent.equipmentProducts.length > 0
      ) {
        updatedContent.equipmentProducts = await Promise.all(
          updatedContent.equipmentProducts.map(async (product) => {
            if (product.image instanceof File) {
              const imageUrl = await uploadImage(product.image);
              return { ...product, image: imageUrl };
            }
            return product;
          })
        );
      }

      // Handle equipment product image uploads
      if (
        updatedContent.equipmentProducts &&
        updatedContent.equipmentProducts.length > 0
      ) {
        updatedContent.equipmentProducts = await Promise.all(
          updatedContent.equipmentProducts.map(async (product) => {
            if (product.image instanceof File) {
              const imageUrl = await uploadImage(product.image);
              return { ...product, image: imageUrl };
            }
            return product;
          })
        );
      }

      // Handle instruction image uploads
      const updatedInstructions = await Promise.all(
        updatedContent.instructions.map(async (instruction) => {
          if (instruction.image instanceof File) {
            const imageUrl = await uploadImage(instruction.image);
            return { ...instruction, image: imageUrl };
          }
          return instruction;
        })
      );

      if (
        updatedContent.image &&
        updatedContent.videoUrl &&
        updatedContent.videoUrl !== "/fantasyloser-default-video.webm"
      ) {
        updatedContent.IsdefaultVideo = false;
      } else {
        updatedContent.IsdefaultVideo = true;
        updatedContent.videoUrl = "";
      }

      updatedContent.instructions = updatedInstructions;

      /* if (typeof videoUrl === "string" && videoUrl !== "") {
        // If videoUrl is set, update the content with the new video URL
        if ( updatedContent.IsdefaultVideo) {
          updatedContent.videoUrl = '';
        } else {
          updatedContent.videoUrl = videoUrl;
        }
      } */

      /* if (updatedContent.IsdefaultVideo) {
        updatedContent.videoUrl = '';
      } */
      console.log("updateContent", updatedContent);
      // Update the content in Firebase
      await updateContent(editedContent.id, updatedContent);
      // Exit editing mode
      setIsEditing(false);

      // Show a success message
      alert("Content updated successfully!");

      // Force a page refresh
      window.location.replace("/admin");
    } catch (error) {
      console.error("Error updating punishment:", error);
      // Show an error message
      alert("An error occurred while updating the content. Please try again.");
    }
  };

  const cancelEdit = () => {
    console.log("Cancel editing");
    // Reset the edited content to the original content
    // Call the function passed from the parent, sending the data
    cancelEditing(true);
    window.location.replace("/admin");
  };

  const deleteVideoSuccess = (url: string) => {
    setVideoUrl("");
    editedContent.videoUrl = "";
  };

  useEffect(() => {
    // Initialize the editedContent state with the content prop
    console.log("Initializing editedContent with content prop:", content);
  }, [content]);

  /* useEffect(() => {
    // Initialize the editedContent state with the content prop
    editedContent.videoUrl = videoUrl;
  }, [videoUrl]); */

  return (
    <main className="container mx-auto px-4 py-8 pt-[50px]">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Edit : {editedContent.title}</h2>
        <button
          onClick={() => navigate("/admin")}
          className="px-4 py-2 bg-[#18f7b1] text-gray-800 rounded hover:bg-[#0fd494]"
        >
          Back
        </button>
      </div>
      <div className="space-y-6 bg-white p-6 rounded-lg shadow-lg px-8 pt-8 pb-8 mb-4">
        {/* Hiding the content type field */}
        <div style={{ display: "none" }}>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Content Type
          </label>
          <select
            name="type"
            value={editedContent.type}
            onChange={handleInputChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 bg-gray-50"
          >
            <option value="punishment">Punishment</option>
            <option value="challenge">Challenge</option>
            <option value="draft">Draft Content</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Title
          </label>
          <div className="flex items-center">
            <input
              type="text"
              name="title"
              maxLength={50}
              value={editedContent.title}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 bg-gray-50"
            />
            <CharCounter value={editedContent.title} max={50} />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            URL Slug
          </label>
          <div className="flex gap-2">
            {/* <div className="flex items-center"> */}
            <input
              type="text"
              name="slug"
              value={editedContent.slug || ""}
              maxLength={100}
              onChange={(e) => {
                const newSlug = generateSlug(e.target.value);
                setEditedContent((prev) => ({ ...prev, slug: newSlug }));
              }}
              className="flex-1 mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 bg-gray-50"
              placeholder="custom-url-slug"
            />
            <CharCounter value={editedContent.slug || ""} max={100} />
            {/* </div> */}
            <button
              type="button"
              onClick={() => {
                const newSlug = generateSlug(editedContent.title);
                setEditedContent((prev) => ({ ...prev, slug: newSlug }));
              }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition duration-200"
            >
              Generate from Title
            </button>
          </div>
          <p className="mt-1 text-sm text-gray-500">
            This will be used in the URL: /
            {editFrom === "draft-ideas" ? "draft-ideas" : "punishments"}/
            <span className="font-mono">
              {editedContent.slug || "slug-example"}
            </span>
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Subtitle
          </label>
          <MarkdownField
            value={editedContent.subtitle}
            onChange={(value) => setSubtitleValue(value || "")}
            placeholder="Enter subtitle with optional formatting"
            minHeight={100}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Main Image
          </label>
          <input
            type="file"
            accept="image/*"
            ref={imageRef}
            onChange={(e) => {
              handleImageChange(e, "main-image");
              setImageType("main-image");
            }}
            className="mt-1 block w-full bg-gray-50"
          />
        </div>
        {showCropper && tempImageUrl && (
          <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center">
            <div className="relative w-[90vw] max-w-2xl h-[90vh] bg-white rounded-lg overflow-hidden shadow-lg">
              <Cropper
                image={tempImageUrl}
                crop={crop}
                zoom={zoom}
                aspect={imageType === "instruction-image" ? 9 / 16 : 9 / 16}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
              <div className="absolute bottom-0 left-0 w-full flex justify-between p-4 bg-white border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowCropper(false);
                    // remove the image
                    if (imageType === "main-image") {
                      if (imageRef.current) {
                        imageRef.current.value = "";
                      }
                    } else if (imageType === "mobile-header-image") {
                      if (mobileHeaderImageRef.current) {
                        mobileHeaderImageRef.current.value = "";
                      }
                    } else if (imageType === "card-image") {
                      if (cardImageRef.current) {
                        cardImageRef.current.value = "";
                      }
                    } else if (imageType === "instruction-image") {
                      // Clear the instruction image if needed
                      setInstructionImageIndex(null);
                    }
                  }}
                  className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleCropConfirm(imageType)}
                  className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                >
                  Crop & Save
                </button>
              </div>
            </div>
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Card Image (Optional)
          </label>
          <input
            type="file"
            accept="image/*"
            ref={cardImageRef}
            onChange={(e) => {
              handleImageChange(e, "card-image");
              setImageType("card-image");
            }}
            className="mt-1 block w-full bg-gray-50"
          />
          <p className="mt-1 text-sm text-gray-500">
            This image will be used for the card on the explore page. If not
            provided, the main image will be used.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Mobile Header Image (Optional)
          </label>
          <input
            type="file"
            accept="image/*"
            ref={mobileHeaderImageRef}
            onChange={(e) => {
              handleImageChange(e, "mobile-header-image");
              setImageType("mobile-header-image");
            }}
            className="mt-1 block w-full bg-gray-50"
          />
          <p className="mt-1 text-sm text-gray-500">
            This image will be used as the header on mobile devices. If not
            provided, the main image will be used.
          </p>
        </div>
        <div className="mb-4">
          <label className="block text-sm font-bold mb-2">Upload Video</label>
          <VideoUploader
            value={videoUrl}
            isDefaultVideo={content.IsdefaultVideo}
            onUploadSuccess={(url) => setVideoUrl(url)}
            onDeleteSuccess={deleteVideoSuccess}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Image Credit
          </label>
          <div className="flex items-center">
            <input
              type="text"
              name="imageCredit"
              maxLength={50}
              value={editedContent.imageCredit || ""}
              onChange={handleInputChange}
              placeholder="e.g., Photo by John Doe"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 bg-gray-50"
            />
            <CharCounter value={editedContent.imageCredit || ""} max={50} />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Image Credit Link
          </label>
          <div className="flex items-center">
            <input
              type="text"
              name="imageCreditLink"
              maxLength={500}
              value={editedContent.imageCreditLink || ""}
              onChange={handleInputChange}
              placeholder="https://www.example.com/@johndoe"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 bg-gray-50"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Image Description
          </label>
          <MarkdownField
            value={editedContent.imageDescription}
            onChange={(value) => setImageDescriptionValue(value || "")}
            placeholder="Describe the image with optional formatting"
            minHeight={150}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Rating
          </label>
          <CustomSelect
            name="flRating"
            value={editedContent.flRating}
            onChange={(value) =>
              handleInputChange({
                target: { name: "flRating", value },
              } as React.ChangeEvent<HTMLSelectElement>)
            }
            options={flRatingOptions}
            placeholder="Select FL Rating"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {editedContent?.type === "draftIdeas" ? "Effort" : "Shame"}
          </label>
          <input
            type="number"
            name="embarrassmentLevel"
            value={editedContent.embarrassmentLevel}
            onChange={handleInputChange}
            min="0"
            max="5"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 bg-gray-50"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {editedContent?.type === "draftIdeas" ? "Skill" : "Danger"}
          </label>
          <input
            type="number"
            name="riskLevel"
            value={editedContent.riskLevel}
            onChange={handleInputChange}
            min="0"
            max="5"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 bg-gray-50"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {editedContent?.type === "draftIdeas" ? "Physicality" : "Effort"}
          </label>
          <input
            type="number"
            name="longTermEffects"
            value={editedContent.longTermEffects}
            onChange={handleInputChange}
            min="0"
            max="5"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 bg-gray-50"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Punishment Location (main heading)
          </label>
          <div className="flex items-center">
            <input
              type="text"
              name="equipmentLocker"
              value={editedContent.equipmentLocker}
              onChange={handleInputChange}
              maxLength={30}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 bg-gray-50"
            />
            <CharCounter value={editedContent.equipmentLocker} max={30} />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Punishment Location (sub heading)
          </label>
          <div className="flex items-center">
            <input
              type="text"
              name="equipmentLockerSubHeading"
              value={editedContent.equipmentLockerSubHeading}
              onChange={handleInputChange}
              maxLength={80}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 bg-gray-50"
            />
            <CharCounter
              value={editedContent.equipmentLockerSubHeading || ""}
              max={80}
            />
          </div>
        </div>

        <div style={{ display: "none" }}>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Equipment Needed
          </label>
          {editedContent.equipmentNeeded.map((item, index) => (
            <div key={index} className="flex items-center gap-2 mb-2">
              <input
                type="text"
                value={item}
                onChange={(e) =>
                  handleArrayInputChange(e, index, "equipmentNeeded")
                }
                className="flex-grow mt-1 block rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 bg-gray-50"
                placeholder={`Equipment ${index + 1}`}
              />
              <button
                type="button"
                onClick={() => {
                  const newEquipment = [...editedContent.equipmentNeeded];
                  newEquipment.splice(index, 1);
                  setEditedContent((prev) => ({
                    ...prev,
                    equipmentNeeded: newEquipment,
                  }));
                }}
                className="p-2 bg-red-500 text-white rounded hover:bg-red-600 transition duration-200"
              >
                ✕
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() =>
              setEditedContent((prev) => ({
                ...prev,
                equipmentNeeded: [...prev.equipmentNeeded, ""],
              }))
            }
            className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition duration-200"
          >
            Add Equipment
          </button>
        </div>

        {/* Equipment Products Section */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Equipment Products
          </label>

          {/* Display current products */}
          {(editedContent.equipmentProducts || []).map((product, index) => (
            <div
              key={index}
              className="mb-4 p-4 border border-gray-200 rounded-md"
            >
              {/* Product Name */}
              <div className="mb-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Product Name
                </label>
                <div className="flex items-center">
                  <input
                    type="text"
                    value={product.name || ""}
                    onChange={(e) => {
                      const updatedProducts = [
                        ...(editedContent.equipmentProducts || []),
                      ];
                      updatedProducts[index] = {
                        ...updatedProducts[index],
                        name: e.target.value,
                      };
                      setEditedContent((prev) => ({
                        ...prev,
                        equipmentProducts: updatedProducts,
                      }));
                    }}
                    maxLength={100}
                    className="w-full p-2 border rounded text-black"
                    placeholder="e.g., Basketball"
                  />
                  <CharCounter value={product.name || ""} max={100} />
                </div>
              </div>

              {/* Product Link */}
              <div className="mb-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Product Link
                </label>
                <input
                  type="url"
                  value={product.link || ""}
                  onChange={(e) => {
                    const updatedProducts = [
                      ...(editedContent.equipmentProducts || []),
                    ];
                    updatedProducts[index] = {
                      ...updatedProducts[index],
                      link: e.target.value,
                    };
                    setEditedContent((prev) => ({
                      ...prev,
                      equipmentProducts: updatedProducts,
                    }));
                  }}
                  className="w-full p-2 border rounded text-black"
                  placeholder="https://example.com/product"
                />
              </div>

              {/* Product Image */}
              <div className="mb-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Product Image
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      const file = e.target.files[0];
                      const updatedProducts = [
                        ...(editedContent.equipmentProducts || []),
                      ];
                      updatedProducts[index] = {
                        ...updatedProducts[index],
                        image: file,
                      };
                      setEditedContent((prev) => ({
                        ...prev,
                        equipmentProducts: updatedProducts,
                      }));
                    }
                  }}
                  className="w-full p-2 border rounded"
                />

                {product.image && (
                  <div className="mt-2 relative">
                    <img
                      src={
                        typeof product.image === "string"
                          ? product.image
                          : URL.createObjectURL(product.image as File)
                      }
                      alt={product.name || "Product"}
                      className="h-32 object-contain border rounded"
                    />
                  </div>
                )}
              </div>

              {/* Remove Button */}
              <button
                type="button"
                onClick={() => {
                  const updatedProducts = [
                    ...(editedContent.equipmentProducts || []),
                  ];
                  updatedProducts.splice(index, 1);
                  setEditedContent((prev) => ({
                    ...prev,
                    equipmentProducts: updatedProducts,
                  }));
                }}
                className="mt-2 px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition duration-200"
              >
                Remove Product
              </button>
            </div>
          ))}

          {/* Add Product Button */}
          <button
            type="button"
            onClick={() => {
              const currentProducts = editedContent.equipmentProducts || [];
              setEditedContent((prev) => ({
                ...prev,
                equipmentProducts: [...currentProducts, { name: "" }],
              }));
            }}
            className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition duration-200"
          >
            Add Equipment Product
          </button>
        </div>

        {/* Legacy Equipment Image/Link (keep for backward compatibility) */}
        <div className="mb-6 border-t border-gray-200 pt-4 mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Legacy Equipment Image/Link (Only for backward compatibility)
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4"></div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Equipment Image
          </label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              if (e.target.files && e.target.files[0]) {
                setEditedContent((prev) => ({
                  ...prev,
                  equipmentImage: e.target.files![0],
                }));
              }
            }}
            className="mt-1 block w-full bg-gray-50 mb-2"
          />
          {editedContent.equipmentImage && (
            <div className="mt-2 relative">
              <div className="w-full h-48 bg-gray-200 rounded-lg overflow-hidden">
                {typeof editedContent.equipmentImage === "string" ? (
                  <img
                    src={editedContent.equipmentImage}
                    alt="Equipment"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <img
                    src={URL.createObjectURL(
                      editedContent.equipmentImage as File
                    )}
                    alt="Equipment Preview"
                    className="w-full h-full object-contain"
                  />
                )}
              </div>
              <button
                type="button"
                onClick={() =>
                  setEditedContent((prev) => ({
                    ...prev,
                    equipmentImage: null,
                  }))
                }
                className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
              >
                ✕
              </button>
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Equipment Image Link (Optional)
          </label>
          <div className="flex items-center gap-2">
            <input
              type="url"
              name="equipmentImageLink"
              value={editedContent.equipmentImageLink || ""}
              onChange={(e) =>
                setEditedContent((prev) => ({
                  ...prev,
                  equipmentImageLink: e.target.value,
                }))
              }
              placeholder="https://example.com/product"
              className="flex-grow mt-1 block rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 bg-gray-50"
            />
            {editedContent.equipmentImageLink && (
              <button
                type="button"
                onClick={() =>
                  setEditedContent((prev) => ({
                    ...prev,
                    equipmentImageLink: "",
                  }))
                }
                className="p-2 bg-red-500 text-white rounded hover:bg-red-600 transition duration-200"
              >
                ✕
              </button>
            )}
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Add a URL to link the equipment image to a product page or more
            information
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Time to Complete (main heading)
          </label>
          <div className="flex items-center">
            <input
              type="text"
              name="timeToComplete"
              value={editedContent.timeToComplete}
              onChange={handleInputChange}
              maxLength={30}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 bg-gray-50"
            />
            <CharCounter value={editedContent.timeToComplete} max={30} />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Time to Complete (sub heading)
          </label>
          <div className="flex items-center">
            <input
              type="text"
              name="timeToCompleteSubHeading"
              value={editedContent.timeToCompleteSubHeading}
              onChange={handleInputChange}
              maxLength={80}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 bg-gray-50"
            />
            <CharCounter
              value={editedContent.timeToCompleteSubHeading ?? ""}
              max={80}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Cost to Complete (main heading)
          </label>
          <div className="flex items-center">
            <input
              type="text"
              name="costToComplete"
              value={editedContent.costToComplete}
              onChange={handleInputChange}
              maxLength={30}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 bg-gray-50"
            />
          </div>
          <CharCounter value={editedContent.costToComplete} max={30} />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Cost to Complete (sub heading)
          </label>
          <div className="flex items-center">
            <input
              type="text"
              name="costToCompleteSubHeading"
              value={editedContent.costToCompleteSubHeading}
              onChange={handleInputChange}
              maxLength={80}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 bg-gray-50"
            />
            <CharCounter
              value={editedContent.costToCompleteSubHeading ?? ""}
              max={80}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Challenge Instructions
          </label>
          {editedContent.instructions.map((instruction, index) => (
            <div
              key={index}
              className="mt-2 mb-4 p-4 border border-gray-200 rounded-md"
            >
              <MarkdownField
                value={instruction.description}
                onChange={(value) => {
                  const newInstructions = [...editedContent.instructions];
                  newInstructions[index].description = value || "";
                  setEditedContent((prev) => ({
                    ...prev,
                    instructions: newInstructions,
                  }));
                }}
                placeholder={`Instruction ${index + 1}`}
                minHeight={150}
              />
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  handleImageChange(e, "instruction-image", index);
                  setImageType("instruction-image");
                }}
                className="mt-1 block w-full bg-gray-50 mb-2"
              />
              {instruction.image && (
                <div className="mt-2 relative inline-block">
                  <img
                    src={
                      typeof instruction.image === "string"
                        ? instruction.image
                        : URL.createObjectURL(instruction.image as File)
                    }
                    alt={`Instruction ${index + 1}`}
                    className="h-32 w-32 object-cover border rounded"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const newInstructions = [...editedContent.instructions];
                      newInstructions[index].image = null;
                      setEditedContent((prev) => ({
                        ...prev,
                        instructions: newInstructions,
                      }));
                    }}
                    className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 w-6 h-6 flex items-center justify-center text-xs"
                  >
                    ✕
                  </button>
                </div>
              )}
              <button
                type="button"
                onClick={() => {
                  const newInstructions = editedContent.instructions.filter(
                    (_, i) => i !== index
                  );
                  setEditedContent((prev) => ({
                    ...prev,
                    instructions: newInstructions,
                  }));
                }}
                className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition duration-200"
              >
                Remove Step
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() =>
              setEditedContent((prev) => ({
                ...prev,
                instructions: [
                  ...prev.instructions,
                  { description: "", image: null },
                ],
              }))
            }
            className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition duration-200"
          >
            Add Instruction
          </button>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Ways to Spice It Up
          </label>
          {editedContent.waysToSpiceUp.map((way, index) => (
            <MarkdownField
              value={way}
              onChange={(value) =>
                handleArrayMarkDownInputChange(
                  value || "",
                  index,
                  "waysToSpiceUp"
                )
              }
              placeholder={`Way to Spice It Up ${index + 1}`}
              minHeight={100}
            />
          ))}
          {/* <button
            type="button"
            onClick={() =>
              setEditedContent((prev) => ({
                ...prev,
                waysToSpiceUp: [...prev.waysToSpiceUp, ""],
              }))
            }
            className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition duration-200"
          >
            Add Way to Spice It Up
          </button> */}
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Social Media Links
          </label>
          <SocialMediaInput onAdd={handleAddSocialLink} />
          <SocialMediaPreview
            links={editedContent.socialMediaLinks || []}
            isEditing={true}
            onDelete={handleDeleteSocialLink}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tags
          </label>
          {editedContent.tags.map((tag, index) => (
            <div key={index} className="flex items-center mb-2">
              <input
                type="text"
                value={tag}
                onChange={(e) => handleArrayInputChange(e, index, "tags")}
                className="flex-grow rounded-l-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 bg-gray-50"
                placeholder={`Tag ${index + 1}`}
              />
              <button
                type="button"
                onClick={() => handleRemoveArrayItem(index, "tags")}
                className="bg-red-500 text-white px-3 py-2 rounded-r-md hover:bg-red-600 transition duration-200"
              >
                Remove
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() =>
              setEditedContent((prev) => ({
                ...prev,
                tags: [...prev.tags, ""],
              }))
            }
            className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition duration-200"
          >
            Add Tag
          </button>
        </div>

        <div className="flex space-x-2">
          <button
            onClick={handleSave}
            className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 transition duration-200"
          >
            Save
          </button>
          <button
            onClick={cancelEdit}
            className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 transition duration-200"
          >
            Cancel
          </button>
        </div>
      </div>
    </main>
  );
};
export default EditPunishment;
